import { requestWrapper } from '@/api';
import { nextTick, type Ref, ref } from 'vue';
import { bufferToUrlEncodedB64, urlEncodedB64ToBuffer } from '@/utils';
import type { LoginResponse } from '@/types/auth';
import type { Pagination, Scroll } from '@/types/option';
import type { QVirtualScroll } from 'quasar';
import type { StringMap } from '@/types/settings';
import { setPagination } from '@/service/index';
import type { List, ListEntry } from '@/types/list';

export async function Submit(field: string | null) {
  const credential: Ref<PublicKeyCredential | null> = ref(null);

  await requestWrapper.get({
    path: '/tfa/begin'
  }).then(async (response: Response) => {
    const options = await response.json();
    const pk = options.publicKey;
    credential.value = await navigator.credentials.create({
      publicKey: {
        ...pk,
        challenge: urlEncodedB64ToBuffer(pk.challenge),
        user: {
          ...pk.user,
          id: urlEncodedB64ToBuffer(pk.user.id)
        }
      }
    }) as PublicKeyCredential | null;
  });

  if (!credential.value) {
    return Promise.reject(Error('no token provided'));
  }

  const crd = credential.value;

  const response = crd.response as AuthenticatorAttestationResponse;

  return requestWrapper.post({
    path: '/tfa/complete',
    body: {
      id: crd.id,
      rawId: bufferToUrlEncodedB64(crd.rawId),
      type: crd.type,
      response: {
        attestationObject: bufferToUrlEncodedB64(response.attestationObject),
        clientDataJSON: bufferToUrlEncodedB64(response.clientDataJSON),
        transports: response.getTransports()
      },
      name: field
    }
  });
}

export async function VerifyTFA(tfa: LoginResponse, url: string, params?: StringMap) {
  if (tfa.session && tfa.session.publicKey) {
    tfa.session.publicKey.challenge = urlEncodedB64ToBuffer(tfa.session.publicKey.challenge as unknown as string);
    tfa.session.publicKey.allowCredentials?.forEach((value) => {
      value.id = urlEncodedB64ToBuffer(value.id as unknown as string);
    });
  }
  const crd = await navigator.credentials
    .get(tfa.session)
    .catch(console.log) as PublicKeyCredential;
  if (!crd) {
    return Promise.reject(Error('no token provided'));
  }
  const response = crd.response as AuthenticatorAssertionResponse;
  return requestWrapper.post({
    path: url,
    body: {
      ...params,
      sessionId: tfa.sessionId,
      id: crd.id,
      rawId: bufferToUrlEncodedB64(crd.rawId),
      type: crd.type,
      response: {
        authenticatorData: bufferToUrlEncodedB64(response.authenticatorData),
        clientDataJson: bufferToUrlEncodedB64(response.clientDataJSON),
        signature: bufferToUrlEncodedB64(response.signature),
        userHandle: bufferToUrlEncodedB64(response.userHandle ?? new ArrayBuffer(0))
      },
      grantType: 'verify'
    }
  });
}

export async function Load(rows: Ref<ListEntry[]>, pagination: Ref<Pagination>, loading: Ref<boolean>, loaded: Ref<boolean>, pageSize: number, count?: boolean, table?: Scroll) {
  const { sortBy, descending } = pagination.value;
  const lastIndex = rows.value.length > 0 ? rows.value.length - 1 : undefined;

  if (loading.value || loaded.value || lastIndex !== table?.to) {
    return;
  }

  loading.value = true;

  await requestWrapper.post({
    path: '/tfa',
    body: {
      lastId: pagination.value.lastId,
      limit: pageSize,
      descending: descending,
      sortBy: sortBy,
      count: count
    }
  }).then(async (response: Response) => {
    setPagination(await response.json() as List, rows, pagination, loaded, count);
  }).finally(() => {
    void nextTick(() => {
      if (table) {
        (table.ref as QVirtualScroll).refresh();
      }
      loading.value = false;
    });
  });
}

export async function Delete(selected: ListEntry[], loading: Ref<boolean>) {
  loading.value = true;
  const ids: string[] = [];
  selected.forEach((value) => {
    ids.push(value.id);
  });
  return requestWrapper.delete({
    path: '/tfa',
    body: { ids: ids }
  }).then((response: Response) => {
    loading.value = false;
    return response;
  });
}

export const TFA = {
  columns: [
    { name: 'index', label: '#', field: 'index' },
    { name: 'id', label: 'ID', field: 'id' },
    { name: 'name', label: 'Name', field: 'name' },
    { name: 'created_at', label: 'Created at', field: 'created_at' },
    { name: 'updated_at', label: 'Updated at', field: 'updated_at' }
  ],
  visible: ['index', 'name', 'created_at', 'updated_at']
};
